import {
  Button,
  Group,
  Modal,
  Stack,
  Text,
  Accordion,
  Checkbox,
  ScrollArea,
  Flex,
} from '@mantine/core';
import { useState, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { File as FileType } from '@/clients/pages/FolderPage/types';

interface ImportFieldsModalProps {
  opened: boolean;
  onClose: () => void;
  currentFile: FileType;
  availableFiles: FileType[];
  onImport: (fieldsToImport: any[]) => void;
}

export const ImportFieldsModal = ({
  opened,
  onClose,
  currentFile,
  availableFiles,
  onImport,
}: ImportFieldsModalProps) => {
  const intl = useIntl();
  const [selectedFieldIds, setSelectedFieldIds] = useState<string[]>([]);
  const [error, setError] = useState<string>('');

  // Get current file's field names (case-insensitive) for filtering
  const currentFieldNames = useMemo(() => {
    return (
      (currentFile as any).fields?.map((f: any) => f.name?.toLowerCase()) || []
    );
  }, [currentFile]);

  // Filter out the current file from available files
  const otherFiles = availableFiles.filter(
    (file) => file.id !== currentFile.id
  );

  // Filter files and fields to only show fields that don't exist in current file
  const filteredFiles = useMemo(() => {
    return otherFiles
      .map((file) => {
        const availableFields = (file.fields || []).filter(
          (field: any) =>
            field.name && !currentFieldNames.includes(field.name.toLowerCase())
        );
        return {
          ...file,
          availableFields,
        };
      })
      .filter((file) => file.availableFields.length > 0);
  }, [otherFiles, currentFieldNames]);

  // Get all field IDs for easy lookup (only for non-duplicate fields)
  const allFieldIds = useMemo(() => {
    const ids: string[] = [];
    filteredFiles.forEach((file) => {
      file.availableFields.forEach((field: any, index: number) => {
        ids.push(`field-${file.id}-${field.name}-${index}`);
      });
    });
    return ids;
  }, [filteredFiles]);

  const handleClose = () => {
    setSelectedFieldIds([]);
    setError('');
    onClose();
  };

  const handleImport = () => {
    if (selectedFieldIds.length === 0) {
      setError(intl.formatMessage({ id: 'selectAtLeastOneField' }));
      return;
    }

    // Get fields from selected field IDs
    const fieldsToImport: any[] = [];
    filteredFiles.forEach((file) => {
      file.availableFields.forEach((field: any, index: number) => {
        const fieldId = `field-${file.id}-${field.name}-${index}`;
        if (selectedFieldIds.includes(fieldId)) {
          fieldsToImport.push(field);
        }
      });
    });

    if (fieldsToImport.length === 0) {
      setError(intl.formatMessage({ id: 'noFieldsToImport' }));
      return;
    }

    // All validations passed
    onImport(fieldsToImport);
    handleClose();
  };

  const handleSelectAll = () => {
    setError('');
    setSelectedFieldIds(allFieldIds);
  };

  const handleDeselectAll = () => {
    setError('');
    setSelectedFieldIds([]);
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={intl.formatMessage({ id: 'importFields' })}
      centered
      size="lg"
    >
      <Stack gap="md">
        {filteredFiles.length === 0 ? (
          <Text size="sm" c="dimmed" ta="center" py="md">
            {otherFiles.length === 0
              ? intl.formatMessage({ id: 'noFilesAvailable' })
              : intl.formatMessage({
                  id: 'noFieldsToImport',
                  defaultMessage:
                    'No fields available to import (all fields already exist)',
                })}
          </Text>
        ) : (
          <>
            <Group gap="xs">
              <Button
                size="xs"
                variant="light"
                onClick={handleSelectAll}
                disabled={selectedFieldIds.length === allFieldIds.length}
              >
                {intl.formatMessage({
                  id: 'selectAll',
                  defaultMessage: 'Select All',
                })}
              </Button>
              <Button
                size="xs"
                variant="light"
                onClick={handleDeselectAll}
                disabled={selectedFieldIds.length === 0}
              >
                {intl.formatMessage({
                  id: 'deselectAll',
                  defaultMessage: 'Deselect All',
                })}
              </Button>
            </Group>

            <ScrollArea h={400}>
              <Accordion variant="separated" radius="md">
                {filteredFiles.map((file) => {
                  const fileFieldIds = file.availableFields.map(
                    (field: any, index: number) =>
                      `field-${file.id}-${field.name}-${index}`
                  );
                  const allFileFieldsSelected = fileFieldIds.every((id) =>
                    selectedFieldIds.includes(id)
                  );
                  const someFileFieldsSelected = fileFieldIds.some((id) =>
                    selectedFieldIds.includes(id)
                  );

                  return (
                    <Accordion.Item key={file.id} value={file.id}>
                      <Accordion.Control
                        onClick={(e) => {
                          // Prevent accordion toggle when clicking on checkbox
                          const target = e.target as HTMLElement;
                          if (
                            target.tagName === 'INPUT' ||
                            target.tagName === 'LABEL' ||
                            target.closest('input[type="checkbox"]') ||
                            target.closest('label') ||
                            target.closest('[data-checkbox-wrapper]')
                          ) {
                            e.preventDefault();
                            e.stopPropagation();
                            return false;
                          }
                        }}
                      >
                        <Flex align="center" gap="xs">
                          <div
                            data-checkbox-wrapper
                            onClick={(e) => {
                              e.stopPropagation();
                            }}
                            onMouseDown={(e) => {
                              e.stopPropagation();
                            }}
                            style={{ pointerEvents: 'auto' }}
                          >
                            <Checkbox
                              checked={allFileFieldsSelected}
                              indeterminate={
                                someFileFieldsSelected && !allFileFieldsSelected
                              }
                              onChange={(e) => {
                                setError('');
                                e.stopPropagation();
                                if (e.currentTarget.checked) {
                                  setSelectedFieldIds([
                                    ...selectedFieldIds.filter(
                                      (id) => !fileFieldIds.includes(id)
                                    ),
                                    ...fileFieldIds,
                                  ]);
                                } else {
                                  setSelectedFieldIds(
                                    selectedFieldIds.filter(
                                      (id) => !fileFieldIds.includes(id)
                                    )
                                  );
                                }
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                              }}
                              onMouseDown={(e) => {
                                e.stopPropagation();
                              }}
                              label={`${file.name} (${intl.formatMessage({
                                id: file.type,
                              })})`}
                            />
                          </div>
                        </Flex>
                      </Accordion.Control>
                      <Accordion.Panel>
                        <Stack gap="xs" pl="md">
                          {file.availableFields.map(
                            (field: any, index: number) => {
                              const fieldId = `field-${file.id}-${field.name}-${index}`;
                              return (
                                <Checkbox
                                  key={fieldId}
                                  label={
                                    field.label ||
                                    field.title ||
                                    field.name ||
                                    `Field ${index + 1}`
                                  }
                                  checked={selectedFieldIds.includes(fieldId)}
                                  onChange={(e) => {
                                    setError('');
                                    if (e.currentTarget.checked) {
                                      setSelectedFieldIds([
                                        ...selectedFieldIds,
                                        fieldId,
                                      ]);
                                    } else {
                                      setSelectedFieldIds(
                                        selectedFieldIds.filter(
                                          (id) => id !== fieldId
                                        )
                                      );
                                    }
                                  }}
                                />
                              );
                            }
                          )}
                        </Stack>
                      </Accordion.Panel>
                    </Accordion.Item>
                  );
                })}
              </Accordion>
            </ScrollArea>
          </>
        )}

        {error && (
          <Text size="sm" c="red">
            {error}
          </Text>
        )}

        <Group justify="space-between" mt="md">
          <Text size="sm" c="dimmed">
            {selectedFieldIds.length > 0 &&
              intl.formatMessage(
                {
                  id: 'fieldsSelected',
                  defaultMessage: '{count} field(s) selected',
                },
                { count: selectedFieldIds.length }
              )}
          </Text>
          <Group>
            <Button variant="subtle" onClick={handleClose}>
              {intl.formatMessage({ id: 'cancel' })}
            </Button>
            <Button
              onClick={handleImport}
              disabled={
                filteredFiles.length === 0 || selectedFieldIds.length === 0
              }
            >
              {intl.formatMessage({ id: 'import' })}
            </Button>
          </Group>
        </Group>
      </Stack>
    </Modal>
  );
};
